/**
 * Wallet preflight (SEP 5.a §L / §M / §N).
 *
 * READ-ONLY wallet operations only:
 *   - detect an injected provider (via wagmi's existing connectors)
 *   - list available connectors
 *   - connect / disconnect
 *   - read the connected account + chain id
 *   - compare against the backend-configured chain id
 *
 * FORBIDDEN in 5.a: approve, purchase, `eth_sendTransaction`,
 * `signTransaction`, automatic network-switch RPCs. The purchase
 * flow proper arrives in 5.b/5.c/5.d.
 */
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { visibleConnectors } from "./wallet-connectors";

export type WalletConnectionStatus =
  | "no_provider"
  | "disconnected"
  | "wrong_chain"
  | "connected";

export interface WalletPreflightState {
  status: WalletConnectionStatus;
  address: `0x${string}` | undefined;
  chainId: number | undefined;
  expectedChainId: number;
  wrongChain: boolean;
  hasInjectedProvider: boolean;
}

export interface WalletPreflightActions {
  connectors: ReturnType<typeof useConnect>["connectors"];
  connect: ReturnType<typeof useConnect>["connect"];
  isConnecting: boolean;
  disconnect: () => void;
}

export interface UseWalletPreflightResult
  extends WalletPreflightState,
    WalletPreflightActions {}

export interface WalletPreflightInputs {
  isConnected: boolean;
  chainId: number | undefined;
  address: `0x${string}` | undefined;
  hasInjectedProvider: boolean;
  expectedChainId: number;
}

/**
 * Pure derivation of the preflight state — used by the hook and
 * directly exercised in unit tests. Keeps the hook a thin wrapper.
 */
export function derivePreflightState(
  i: WalletPreflightInputs,
): WalletPreflightState {
  let status: WalletConnectionStatus;
  if (!i.isConnected) {
    status = i.hasInjectedProvider ? "disconnected" : "no_provider";
  } else if (i.chainId !== i.expectedChainId) {
    status = "wrong_chain";
  } else {
    status = "connected";
  }
  return {
    status,
    address: i.address,
    chainId: i.chainId,
    expectedChainId: i.expectedChainId,
    wrongChain: i.isConnected && i.chainId !== i.expectedChainId,
    hasInjectedProvider: i.hasInjectedProvider,
  };
}

/**
 * Derive the preflight state from wagmi hooks + backend-configured chain.
 *
 * `expectedChainId` MUST come from /sale/config.chain_id (backend
 * authority). Frontend NEVER hardcodes a target chain.
 */
export function useWalletPreflight(
  expectedChainId: number,
): UseWalletPreflightResult {
  const { address, chainId, isConnected } = useAccount();
  const { connectors, connect, isPending } = useConnect();
  const { disconnect } = useDisconnect();

  const derived = derivePreflightState({
    isConnected,
    chainId,
    address: address as `0x${string}` | undefined,
    hasInjectedProvider: detectInjectedProvider(),
    expectedChainId,
  });

  return {
    ...derived,
    connectors: visibleConnectors(connectors, detectInjectedProvider()),
    connect,
    isConnecting: isPending,
    disconnect,
  };
}

/**
 * Pure detection helper — usable both inside components and in tests.
 * SSR-safe: returns false when `window` is undefined.
 */
export function detectInjectedProvider(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as unknown as { ethereum?: unknown }).ethereum);
}
